'use client';

import dynamic from 'next/dynamic';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useApplePayDeviceSupported } from '../../engine/apple-pay/deviceSupport';
import type { ExpressApplePayHostProps } from './ExpressApplePayCheckout';

/**
 * The heavier checkout subtree (engine + firebase guest auth + Stripe.js) is code-split
 * so it never lands in the initial bio bundle — it's imported only after the device gate
 * passes. `ssr: false`: `ApplePaySession` is client-only and the subtree is purely
 * interactive, so there's nothing to server-render.
 */
const ExpressApplePayCheckout = dynamic(
  () =>
    import('./ExpressApplePayCheckout').then(m => m.ExpressApplePayCheckout),
  { ssr: false },
);

/**
 * Pre-gate for the express Apple Pay button. Renders nothing — loading neither the
 * engine (no guest account provisioned) nor Stripe.js — unless BOTH hold:
 *
 *  1. **Device support** — a synchronous, Stripe-free `ApplePaySession.canMakePayments()`
 *     check, so non-Apple devices mount nothing.
 *  2. **Auth resolved** — `useCurrentUserId()` is tri-state (`undefined` while Firebase auth
 *     is still initializing → `null` anonymous → uid). We MUST wait for it to leave
 *     `undefined`: the engine provisions a guest account and fires its first authenticated
 *     order preview during `initialize()`, and if that runs against not-yet-warm auth the
 *     preview goes out with no token (a `401`, surfaced as `pricing_unavailable`). Waiting
 *     until auth has resolved guarantees the guest/user is established first, and the resolved
 *     id (uid or `null`) is injected into the engine.
 *
 * The acting user id is resolved HERE via `useCurrentUserId` — callers never pass it (a
 * gated surface is always signed in, so the hook yields its uid once resolved). On an
 * Apple-Pay-capable device with auth resolved it lazily loads and mounts
 * {@link ExpressApplePayCheckout}.
 */
export function ExpressApplePayGate(props: ExpressApplePayHostProps) {
  const supported = useApplePayDeviceSupported();
  const userId = useCurrentUserId();

  if (!supported) {
    return null;
  }
  // Wait for Firebase auth to resolve before mounting the engine (see docstring).
  if (userId === undefined) {
    return null;
  }

  return <ExpressApplePayCheckout userId={userId} {...props} />;
}
